import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl, API_CONFIG } from '../../config/api';
import axios from 'axios';

interface DocFiscalImpuesto {
  id: number;
  linea_id: number;
  idimpuesto?: number;
  codigo?: string;
  tarifa?: number;
  monto?: number;
  iddocumento?: number;
  idlinea?: number;
  codigoimpuesto?: string;
}

interface DocFiscalLinea {
  id: number;
  encabezado_id: number;
  idlinea?: number;
  numlinea?: number;
  iddocumento?: number;
  tipo?: string;
  codigo?: string;
  detalle?: string;
  unidadmedida?: string;
  cantidad?: number;
  preciounitario?: number;
  monto?: number;
  descuento?: number;
  porcdescuento?: number;
  naturalezadescuento?: string;
  subtotal?: number;
  impuesto?: number;
  totallinea?: number;
  codfiscal?: string;
  impuestos: DocFiscalImpuesto[];
}

interface HaciendaInfo {
  consecutivo?: string;
  clave_numerica?: string;
  estado_aceptacion?: string;
  fecha_envio?: string;
  fecha_consulta?: string;
}

interface DocFiscalEncabezado {
  id: number;
  envio_id: number;
  iddocumento: number;
  tipo?: string;
  referenciaexterna?: string;
  referenciainterna?: string;
  fecha?: string;
  moneda?: string;
  tipocambio?: number;
  totalimpuesto?: number;
  totaldescuento?: number;
  totalfactura?: number;
  condicionventa?: string;
  mediopago?: string;
  documentopadre?: string;
  numlote?: string;
  consecutivo?: string;
  clavenumerica?: string;
  numarrendamiento?: number;
  numcuota?: number;
  mcaprocesado?: string;
  fechavencimiento?: string;
  subtotal?: number;
  clavereferencia?: string;
  codempresa?: string;
  tipoidentificacion?: string;
  numeroidentificacion?: string;
  nombrereceptor?: string;
  emailreceptor?: string;
  numerotelefono?: string;
  notas?: string;
  dirreceptor?: string;
  lineas: DocFiscalLinea[];
  hacienda_info?: HaciendaInfo;
}

const DocumentoDetalle = () => {
  const { iddocumento } = useParams<{ iddocumento: string }>();
  const navigate = useNavigate();
  const [documento, setDocumento] = useState<DocFiscalEncabezado | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (iddocumento) {
      loadDocumentoDetalle();
    }
  }, [iddocumento]);

  const loadDocumentoDetalle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${buildApiUrl(API_CONFIG.CICLA.DOCUMENTO_DETALLE)}/${iddocumento}/detalle`
      );
      setDocumento(response.data.encabezado);
    } catch (err) {
      setError('Error al cargar los detalles del documento');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount === undefined || amount === null) return '-';
    
    // Determinar la moneda y configuración regional
    let currencyCode = 'CRC';
    let locale = 'es-CR';
    
    switch (currency?.toUpperCase()) {
      case 'USD':
        currencyCode = 'USD';
        locale = 'en-US';
        break;
      case 'EUR':
        currencyCode = 'EUR';
        locale = 'de-DE';
        break;
      case 'CRC':
      default:
        currencyCode = 'CRC';
        locale = 'es-CR';
        break;
    }
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '-';
    return new Intl.NumberFormat('es-CR').format(num);
  };

  const getHaciendaStatusColor = (status?: string) => {
    switch (status) {
      case 'aceptado':
        return 'bg-green-100 text-green-800';
      case 'rechazado':
        return 'bg-red-100 text-red-800';
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !documento) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Documento no encontrado'}</p>
          <button
            onClick={() => navigate('/cicla-invoices')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
          >
            Volver a Facturas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/cicla-invoices')}
                className="flex items-center text-primary-600 hover:text-primary-700 mb-4"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Volver a Facturas
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Documento Fiscal #{documento.iddocumento}
              </h1>
              <p className="mt-2 text-gray-600">
                Detalles completos del documento fiscal
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Estado</div>
              <div className="text-lg font-semibold text-gray-900">
                {documento.tipo || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Información del Encabezado */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Información General</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">ID Documento</label>
                <p className="mt-1 text-sm text-gray-900">{documento.iddocumento}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Referencia Externa</label>
                <p className="mt-1 text-sm text-gray-900">{documento.referenciaexterna || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Referencia Interna</label>
                <p className="mt-1 text-sm text-gray-900">{documento.referenciainterna || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Fecha</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(documento.fecha)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Moneda</label>
                <p className="mt-1 text-sm text-gray-900">{documento.moneda || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Tipo de Cambio</label>
                <p className="mt-1 text-sm text-gray-900">{formatNumber(documento.tipocambio)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Información de Hacienda */}
        {documento.hacienda_info && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Información de Hacienda</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Consecutivo</label>
                  <p className="mt-1 text-sm text-gray-900">{documento.hacienda_info.consecutivo || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Clave Numérica</label>
                  <p className="mt-1 text-sm text-gray-900">{documento.hacienda_info.clave_numerica || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Estado de Aceptación</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${getHaciendaStatusColor(documento.hacienda_info.estado_aceptacion)}`}>
                    {documento.hacienda_info.estado_aceptacion || 'N/A'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Fecha de Envío</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(documento.hacienda_info.fecha_envio)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Última Consulta</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(documento.hacienda_info.fecha_consulta)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Información del Receptor */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Información del Receptor</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Nombre</label>
                <p className="mt-1 text-sm text-gray-900">{documento.nombrereceptor || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-sm text-gray-900">{documento.emailreceptor || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Teléfono</label>
                <p className="mt-1 text-sm text-gray-900">{documento.numerotelefono || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Tipo Identificación</label>
                <p className="mt-1 text-sm text-gray-900">{documento.tipoidentificacion || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Número Identificación</label>
                <p className="mt-1 text-sm text-gray-900">{documento.numeroidentificacion || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Dirección</label>
                <p className="mt-1 text-sm text-gray-900">{documento.dirreceptor || '-'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Totales */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Totales</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Subtotal</label>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(documento.subtotal, documento.moneda)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Total Impuestos</label>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(documento.totalimpuesto, documento.moneda)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Total Descuentos</label>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(documento.totaldescuento, documento.moneda)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Total Factura</label>
                <p className="mt-1 text-lg font-semibold text-primary-600">
                  {formatCurrency(documento.totalfactura, documento.moneda)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Líneas del Documento */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Líneas del Documento ({documento.lineas.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Línea
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Detalle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cantidad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Unit.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subtotal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Línea
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documento.lineas.map((linea) => (
                  <tr key={linea.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {linea.numlinea || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {linea.codigo || '-'}
                      {linea.codfiscal && (
                          <div className="text-xs text-gray-500 mt-1">
                            Cabys: {linea.codfiscal}
                          </div>
                        )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{linea.detalle || '-'}</div>
                        {linea.impuestos && linea.impuestos.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Impuestos: {linea.impuestos.map(imp => 
                              `${imp.codigo}(${formatNumber(imp.tarifa)}%)`
                            ).join(', ')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(linea.cantidad)} {linea.unidadmedida}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(linea.preciounitario, documento.moneda)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(linea.subtotal, documento.moneda)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(linea.totallinea, documento.moneda)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentoDetalle; 